import ReactECharts from 'echarts-for-react';
import React from 'react';
import { useColorStore } from '@/store/colorStore';

type ColorKey =
  | 'accentBlue'
  | 'accentYellow'
  | 'accentGrape'
  | 'accentCyan'
  | 'accentOrange'
  | 'accentSoftGreen'
  | 'accentSoftRed'
  | 'accentSoftYellow'
  | 'accentSoftGrape'
  | 'danger'
  | 'warning'
  | 'greyPrimary';

interface ChartData {
  label: string;
  lineValue: number;
  barValue: number;
}

interface Props {
  data: ChartData[];
  lineColor?: ColorKey | string;
  barColor?: ColorKey | string;
  height?: number | string;
  // Tooltip series labels + units. Defaults match the component's volume (line)
  // / avg-speed (bar) usage; the bar y-axis is already labelled "Km/h".
  lineName?: string;
  barName?: string;
  lineUnit?: string;
  barUnit?: string;
}

const BarLineChart = ({
  data,
  lineColor = 'accentBlue',
  barColor = 'accentBlue',
  height = '100%',
  lineName = 'Volume',
  barName = 'Avg Speed',
  lineUnit = '',
  barUnit = 'km/h',
}: Props) => {
  const { NEWCOLOR, chart } = useColorStore();

  const getColor = (color?: ColorKey | string) => {
    if (!color) return '#007AFF';

    if (color in NEWCOLOR) {
      return NEWCOLOR[color as ColorKey];
    }

    return color;
  };

  const option = {
    animationDuration: 600,

    tooltip: {
      trigger: 'axis',
      // Axis trigger passes an array of the hovered category's series points.
      // Append the unit per series so avg-speed reads "… km/h", volume stays bare.
      formatter: (params: unknown) => {
        const points = Array.isArray(params) ? params : [params];
        const first = points[0] as { axisValueLabel?: string; name?: string } | undefined;
        const header = first?.axisValueLabel ?? first?.name ?? '';
        const rows = points.map((p) => {
          const point = p as { seriesName?: string; value?: number; marker?: string };
          const unit = point.seriesName === barName ? barUnit : lineUnit;
          const value = point.value ?? 0;
          return `${point.marker ?? ''}${point.seriesName ?? ''}: ${value}${unit ? ` ${unit}` : ''}`;
        });
        return [header, ...rows].join('<br/>');
      },
    },

    grid: [
      {
        left: '9%',
        right: '2%',
        top: '5%',
        bottom: '0%',
        height: '35%',
      },
      {
        left: '7%',
        right: '0%',
        top: '52%',
        bottom: '0%',
        height: '44%',
      },
    ],

    xAxis: [
      {
        type: 'category',
        gridIndex: 0,
        boundaryGap: false,
        data: data.map((d) => d.label),

        axisTick: {
          show: false,
        },

        axisLine: {
          lineStyle: {
            color: chart.axisLine,
          },
        },

        axisLabel: {
          color: chart.axisLabel,
          fontSize: 11,
        },
      },

      {
        type: 'category',
        gridIndex: 1,
        boundaryGap: true,
        data: data.map((d) => d.label),

        axisTick: {
          show: false,
        },

        axisLine: {
          show: false,
        },

        axisLabel: {
          show: false,
        },
      },
    ],

    yAxis: [
      {
        type: 'value',
        gridIndex: 0,

        splitLine: {
          lineStyle: {
            color: chart.splitLine,
          },
        },

        axisLabel: {
          color: chart.axisLabel,
          margin: 25,
        },

        axisLine: {
          show: false,
        },

        axisTick: {
          show: false,
        },
      },

      {
        type: 'value',
        inverse: true,
        min: 0,
        gridIndex: 1,

        splitLine: {
          lineStyle: {
            color: chart.splitLine,
          },
        },

        axisLabel: {
          formatter: '{value} Km/h',
          color: chart.axisLabel,
        },

        axisLine: {
          show: false,
        },

        axisTick: {
          show: false,
        },
      },
    ],

    series: [
      {
        type: 'line',
        name: lineName,
        xAxisIndex: 0,
        yAxisIndex: 0,

        smooth: true,

        symbol: 'none',

        data: data.map((d) => d.lineValue),

        lineStyle: {
          width: 4,
          color: getColor(lineColor),
        },

        areaStyle: {
          opacity: 0.15,
          color: getColor(lineColor),
        },
      },

      {
        type: 'bar',
        name: barName,

        xAxisIndex: 1,
        yAxisIndex: 1,

        data: data.map((d) => d.barValue),

        barWidth: 22,

        itemStyle: {
          color: getColor(barColor),
          borderRadius: [4, 4, 0, 0],
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{
        width: '100%',
        height,
      }}
    />
  );
};

export default BarLineChart;
